import { Injectable } from '@angular/core';

import { DevicePlatform, FeedName, LogoutSurface } from '../../shared/enums';
import { BreadcrumbCategory, ErrorSource, StorageFailure } from './enums';
import { BreadcrumbModel, SafeProperties, TelemetryProperties } from './models';

const EVENTS: ReadonlySet<string> = new Set([
  'auth_state_changed', 'login_succeeded', 'logout_performed', 'credentials_revoked', 'credentials_revocation_suspected',
  'oauth_diagnostic', 'oauth_warning',
  'feed_succeeded', 'feed_failed', 'feed_health', 'network_online', 'network_offline', 'heartbeat', 'error',
  'display_unclean_restart', 'session_started', 'session_closed', 'storage_failure', 'playback_stalled', 'slide_overrun',
  'slide_changed', 'page_view', 'trace', 'legacy_event', 'nightly_reload_scheduled', 'nightly_reload', 'runtime_started',
  'runtime_stopped', 'sw_update', 'channel_switch', 'refresh_started', 'refresh_succeeded', 'refresh_failed',
  'config_retry_scheduled', 'service_worker_check_failed', 'service_worker_version', 'service_worker_unrecoverable',
  'memory_pressure_reload'
]);
const CATEGORIES: readonly BreadcrumbCategory[] = Object.values(BreadcrumbCategory);
// STATES, KINDS and REASONS stay literal on purpose: they accept legacy and PascalCase values that
// no enum carries, and this is an allow-list, so narrowing them silently drops live telemetry.
const STATES: readonly string[] = [
  'starting', 'playing', 'offline', 'auth-recovering', 'empty', 'stalled', 'valid', 'refreshing', 'recovering', 'revoked', 'anonymous'
];
const KINDS: readonly string[] = [
  'unknown', 'news', 'priority-news', 'meals', 'activities', 'youtube-activities', 'youtube-live', 'scheduled-video',
  'activity-overview', 'birthday', 'birthdays', 'dining', 'Unknown', 'News', 'PriorityNews', 'Meals', 'Activities', 'YoutubeActivities',
  'YoutubeLive', 'ScheduledVideo', 'ActivityOverview', 'Birthday'
];
const FEEDS: readonly string[] = Object.values(FeedName);
const REASONS: readonly string[] = [
  'user-initiated', 'credentials-revoked', 'unexpected', 'nightly-reload', 'sw-update', 'manual', 'channel-switch',
  'watchdog-restart', 'pagehide', 'invalid-config', 'config-unavailable', 'check-failed', 'unrecoverable',
  'memory-pressure'
];
const NUMBERS: ReadonlySet<string> = new Set([
  'uptimeMs', 'runtimeUptimeMs', 'playlistLength', 'feedSlideCount', 'availableSlides', 'slidesShownSinceStart', 'lastSuccessAgoMs',
  'consecutiveFailures', 'lastRefreshAgoMs', 'refreshFailures', 'usedJsHeapMb', 'heapLimitMb', 'limitMb', 'lastHeapMb',
  'offlineEventsSinceStart', 'activeTimers', 'activeTimersAtDeath', 'screenWidth', 'screenHeight', 'pixelRatio',
  'deviceMemoryGb', 'previousUptimeMs', 'consecutiveFeedFailures', 'tokenAgeMs', 'consecutiveUnauthorized',
  'consecutive401Count', 'consecutive403Count', 'durationMs', 'value', 'failures', 'delayMs', 'signals', 'required'
]);
// A token that already expired reports a negative remaining lifetime, and that is exactly the case
// worth alerting on, so these cannot go through the non-negative check the other numbers use.
const SIGNED_NUMBERS: ReadonlySet<string> = new Set(['accessTokenExpiresInMs']);
const BOOLEANS: ReadonlySet<string> = new Set([
  'online', 'tokenValid', 'suspectedOom', 'hasFullscreenContent', 'dsWeb', 'closedCleanly'
]);
const NULLABLE: readonly string[] = ['lastSuccessAgoMs', 'lastRefreshAgoMs', 'currentSlideKind', 'accessTokenExpiresInMs'];
const ENUMS: Readonly<Record<string, readonly string[]>> = {
  state: STATES, previousState: STATES, lastState: STATES, authState: STATES,
  currentSlideKind: KINDS, lastSlideKind: KINDS, displayType: KINDS, feed: FEEDS,
  reason: REASONS,
  surface: Object.values(LogoutSurface),
  region: ['EU', 'CA', 'US', '-'],
  platform: Object.values(DevicePlatform),
  classification: ['type', 'range', 'syntax', 'reference', 'timeout', 'network', 'http', 'error', 'unknown'],
  context: ['effects', 'global', 'unhandled-rejection', 'http', 'runtime'],
  source: Object.values(ErrorSource),
  versionEvent: ['VERSION_DETECTED', 'VERSION_READY', 'VERSION_INSTALLATION_FAILED', 'NO_NEW_VERSION_DETECTED', 'VERSION_FAILED'],
  storageFailure: Object.values(StorageFailure)
};
const VERSION_KEYS: ReadonlySet<string> = new Set(['appVersion', 'Build', 'Version', 'chromiumVersion', 'nativeShell']);
const VERSION_PATTERN: RegExp = /^\d{1,9}(?:\.\d{1,9}){0,3}$/;
const ID_PATTERN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// A stack is reduced to bundle file names and positions, which map back through the build's source map;
// the message, URLs and function names never leave the device.
const ERROR_NAME_PATTERN: RegExp = /^[A-Z][A-Za-z0-9]{0,40}$/;
const STACK_FRAME_PATTERN: RegExp = /([\w.-]{1,80}\.m?js):(\d{1,9}):(\d{1,9})\)?$/;
const FRAMES_PATTERN: RegExp = /^[\w.-]{1,80}\.m?js:\d{1,9}:\d{1,9}(?: < [\w.-]{1,80}\.m?js:\d{1,9}:\d{1,9}){0,4}$/;
const MAX_FRAMES = 5;
const MAX_BREADCRUMBS = 200;
const MAX_BREADCRUMB_JSON = 250_000;
const MAX_FEED_JSON = 10_000;

// Allow-list, never a deny-list: an unrecognised key is dropped, so a new field cannot leak resident data.
@Injectable({ providedIn: 'root' })
export class TelemetryScrubberService {
  public isTelemetryId(value: unknown): value is string {
    return typeof value === 'string' && ID_PATTERN.test(value);
  }

  public safeEventName(value: unknown, fallback: string = 'legacy_event'): string {
    return typeof value === 'string' && EVENTS.has(value) ? value : fallback;
  }

  public record(value: unknown): TelemetryProperties {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as TelemetryProperties : {};
  }

  public scrub(input: unknown): SafeProperties {
    const result: SafeProperties = {};

    for (const [key, value] of Object.entries(this.record(input))) {
      const safe = this.safeValue(key, value);

      if (safe !== undefined) {
        result[key] = safe;
      }
    }

    return result;
  }

  public scrubFeeds(input: unknown): Record<string, { lastSuccessAgoMs: number | null; consecutiveFailures: number }> {
    const source = this.parseJson(input, MAX_FEED_JSON);
    const result: Record<string, { lastSuccessAgoMs: number | null; consecutiveFailures: number }> = {};

    for (const [feed, health] of Object.entries(this.record(source))) {
      if (!FEEDS.includes(feed)) {
        continue;
      }

      const item = this.record(health);
      const failures = item['consecutiveFailures'];
      const age = item['lastSuccessAgoMs'] ?? null;

      if (this.isCount(failures) && (age === null || this.isPositiveNumber(age))) {
        result[feed] = { lastSuccessAgoMs: typeof age === 'number' ? age : null, consecutiveFailures: failures as number };
      }
    }

    return result;
  }

  public scrubBreadcrumbs(input: unknown[]): BreadcrumbModel[] {
    return input.slice(-MAX_BREADCRUMBS).map(value => {
      const item = this.record(value);
      const data = Object.entries(this.record(item['data'])).filter(([key]) => key !== 'breadcrumbs');

      return {
        t: this.isPositiveNumber(item['t']) ? item['t'] as number : 0,
        cat: CATEGORIES.includes(item['cat'] as BreadcrumbCategory) ? item['cat'] as BreadcrumbCategory : BreadcrumbCategory.Lifecycle,
        msg: this.safeEventName(item['msg'], 'trace'),
        data: this.scrub(Object.fromEntries(data))
      };
    });
  }

  public classifyError(error: unknown): string {
    if (error instanceof TypeError) {
      return 'type';
    }

    if (error instanceof RangeError) {
      return 'range';
    }

    if (error instanceof SyntaxError) {
      return 'syntax';
    }

    if (error instanceof ReferenceError) {
      return 'reference';
    }

    // RxJS's `timeout` raises this; it is the only sign of a request that never got an answer.
    if (error instanceof Error && error.name === 'TimeoutError') {
      return 'timeout';
    }

    if (error instanceof Error) {
      return 'error';
    }

    const status = this.record(error)['status'];

    // Status 0 is a request the browser could not send or complete at all, not a server answer.
    return typeof status === 'number' ? (status === 0 ? 'network' : 'http') : 'unknown';
  }

  public errorName(error: unknown): string | undefined {
    const name = this.record(error)['name'];

    return typeof name === 'string' && ERROR_NAME_PATTERN.test(name) ? name : undefined;
  }

  public errorFrames(error: unknown): string | undefined {
    if (!(error instanceof Error) || typeof error.stack !== 'string') {
      return undefined;
    }

    // V8 prefixes the stack with the message, which may quote resident data, so only the lines after it are read.
    const header = error.message ? `${error.name}: ${error.message}` : error.name;
    const body = error.stack.startsWith(header) ? error.stack.slice(header.length) : error.stack;
    const frames = body.split('\n')
      .map(line => STACK_FRAME_PATTERN.exec(line.trim()))
      .filter((match): match is RegExpExecArray => match !== null)
      .slice(0, MAX_FRAMES)
      .map(([, file, line, column]) => `${file}:${line}:${column}`);

    return frames.length ? frames.join(' < ') : undefined;
  }

  // App Insights properties are flat, so the nested heartbeat is spread into one level before scrubbing.
  public scrubHeartbeat(payload: TelemetryProperties): SafeProperties {
    const device = this.record(payload['device']);
    const screen = this.record(device['screen']);
    const memory = this.record(payload['memory']);

    return this.scrub({
      ...payload, ...device, ...this.record(payload['auth']), ...this.record(payload['network']), ...memory,
      screenWidth: screen['w'] ?? payload['screenWidth'],
      screenHeight: screen['h'] ?? payload['screenHeight'],
      pixelRatio: screen['dpr'] ?? payload['pixelRatio'],
      heapLimitMb: memory['limitMb'] ?? payload['heapLimitMb'],
      activeTimers: this.record(payload['timers'])['active'] ?? payload['activeTimers']
    });
  }

  private safeValue(key: string, value: unknown): string | number | boolean | null | undefined {
    if (NUMBERS.has(key) && this.isPositiveNumber(value)) {
      return value as number;
    }

    if (SIGNED_NUMBERS.has(key) && this.isSignedNumber(value)) {
      return value as number;
    }

    if (NULLABLE.includes(key) && value === null) {
      return null;
    }

    if (BOOLEANS.has(key) && typeof value === 'boolean') {
      return value;
    }

    if (Object.prototype.hasOwnProperty.call(ENUMS, key) && typeof value === 'string' && ENUMS[key].includes(value)) {
      return value;
    }

    if (VERSION_KEYS.has(key) && typeof value === 'string' && VERSION_PATTERN.test(value)) {
      return value;
    }

    if (key === 'Build' && value === 'local') {
      return value;
    }

    if ((key === 'displayId' || key === 'sessionId') && this.isTelemetryId(value)) {
      return value;
    }

    if (key === 'httpStatus' && typeof value === 'number' && Number.isInteger(value) && value >= 100 && value <= 599) {
      return value;
    }

    if (key === 'errorName' && typeof value === 'string' && ERROR_NAME_PATTERN.test(value)) {
      return value;
    }

    if (key === 'frames' && typeof value === 'string' && FRAMES_PATTERN.test(value)) {
      return value;
    }

    return this.safeCollection(key, value);
  }

  private safeCollection(key: string, value: unknown): string | undefined {
    if (key === 'breadcrumbs') {
      const breadcrumbs = this.parseJson(value, MAX_BREADCRUMB_JSON);

      return Array.isArray(breadcrumbs) ? JSON.stringify(this.scrubBreadcrumbs(breadcrumbs)) : undefined;
    }

    if (key === 'feeds') {
      return JSON.stringify(this.scrubFeeds(value));
    }

    return undefined;
  }

  private parseJson(value: unknown, maximumLength: number): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    if (value.length > maximumLength) {
      return undefined;
    }

    try {
      return JSON.parse(value) as unknown;
    } catch {
      return undefined;
    }
  }

  private isPositiveNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER;
  }

  private isSignedNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER;
  }

  private isCount(value: unknown): boolean {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }
}
